import { useEffect, useState, type FormEvent } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import { X, CheckCircle2 } from 'lucide-react';
import { ApiError, submitLead } from '../api';
import PhoneField from './PhoneField';
import { useTheme } from '../context/ThemeContext';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

interface LeadFormProps {
  open: boolean;
  onClose: () => void;
}

export default function LeadForm({ open, onClose }: LeadFormProps) {
  const { resolved } = useTheme();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [phone, setPhone] = useState<string | undefined>();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Reset all state whenever the modal is reopened.
  useEffect(() => {
    if (open) {
      setName('');
      setEmail('');
      setBusinessName('');
      setPhone(undefined);
      setCaptchaToken(null);
      setSubmitting(false);
      setError(null);
      setSuccess(false);
    }
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!phone) {
      setError('Please enter a phone number.');
      return;
    }
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setError('Please complete the captcha before submitting.');
      return;
    }

    setSubmitting(true);
    try {
      await submitLead({
        name,
        email,
        phone,
        businessName,
        captchaToken: captchaToken ?? '',
      });
      setSuccess(true);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        setError(
          err.fieldError('name') ||
            err.fieldError('email') ||
            err.fieldError('phone') ||
            err.fieldError('businessName') ||
            err.message,
        );
      } else {
        setError('Something went wrong. Please try again.');
      }
      setCaptchaToken(null);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'mt-1 block w-full rounded-xl border border-strong px-4 py-3 shadow-sm focus:border-brand-600 focus:ring-brand-600 sm:text-sm';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="animate-in fade-in zoom-in bg-surface w-full max-w-md overflow-hidden rounded-2xl shadow-xl duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-subtle flex items-center justify-between border-b px-6 py-4">
          <h3 className="text-fg text-lg font-bold">
            {success ? 'Request received' : 'Get started with Calendyz'}
          </h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="hover:bg-muted rounded-full p-2 transition-colors"
          >
            <X className="text-fg-subtle h-5 w-5" />
          </button>
        </div>

        {success ? (
          <div className="flex flex-col items-center px-6 py-10 text-center">
            <CheckCircle2 className="text-success h-12 w-12" />
            <p className="text-fg mt-4 font-semibold">Thanks for reaching out!</p>
            <p className="text-fg-muted mt-1 text-sm">
              Our team will contact you shortly to help set up your salon profile.
            </p>
            <button
              onClick={onClose}
              className="bg-brand-600 hover:bg-brand-700 mt-6 rounded-xl px-6 py-3 text-sm font-medium text-white transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 px-6 py-5">
            <p className="text-fg-muted text-sm">
              Tell us a bit about you and we'll be in touch to get your salon onboarded.
            </p>

            {error && (
              <div className="border-danger/25 bg-danger-soft text-danger rounded-xl border p-3 text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="lead-name" className="text-fg-secondary block text-sm font-medium">
                Full Name
              </label>
              <input
                type="text"
                id="lead-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputClass}
                placeholder="Jane Doe"
              />
            </div>

            <div>
              <label
                htmlFor="lead-business"
                className="text-fg-secondary block text-sm font-medium"
              >
                Business Name
              </label>
              <input
                type="text"
                id="lead-business"
                required
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                className={inputClass}
                placeholder="Glow Hair Studio"
              />
            </div>

            <div>
              <label htmlFor="lead-email" className="text-fg-secondary block text-sm font-medium">
                Email
              </label>
              <input
                type="email"
                id="lead-email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
                placeholder="jane@example.com"
              />
            </div>

            <div>
              <label htmlFor="lead-phone" className="text-fg-secondary block text-sm font-medium">
                Phone Number
              </label>
              <PhoneField
                id="lead-phone"
                value={phone}
                onChange={setPhone}
                className="mt-1"
                selectClassName="rounded-xl border border-strong shadow-sm focus:border-brand-600 focus:ring-brand-600 sm:text-sm px-3 py-3"
                inputClassName="flex-1 rounded-xl border border-strong shadow-sm focus:border-brand-600 focus:ring-brand-600 sm:text-sm px-4 py-3"
              />
            </div>

            {TURNSTILE_SITE_KEY && (
              <div className="flex justify-center pt-1">
                {/* `resolved`, not 'auto': auto follows the OS, which would
                    show a dark widget to someone who forced Light. */}
                <Turnstile
                  key={resolved}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={setCaptchaToken}
                  onExpire={() => setCaptchaToken(null)}
                  onError={() => setCaptchaToken(null)}
                  options={{ theme: resolved }}
                />
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || (!!TURNSTILE_SITE_KEY && !captchaToken)}
              className="bg-brand-600 hover:bg-brand-700 w-full rounded-xl px-4 py-3 font-medium text-white transition-colors disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
